import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Divider,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@/platform/ui';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import EmploymentPeriods from './EmploymentPeriods';

const SroiEntitlementCard = ({ entitlement, onEdit }) => {
    const { auth, employee } = usePage().props;
    const { confirm, setLoading } = useConfirmationStore();
    const canUpdate = auth?.permissions?.includes('update sroi');
    const canDelete = auth?.permissions?.includes('delete sroi');
    const employments = entitlement.overlapping_employments ?? [];

    const handleDelete = () =>
        confirm({
            title: 'SROI-recht verwijderen?',
            description:
                'Het recht en de koppeling met de PSO-prestatieladder worden verwijderd. Dit kun je niet ongedaan maken.',
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                setLoading(true);

                router.delete(
                    `/customers/${employee.customer_id}/employees/${employee.id}/sroi/${entitlement.id}`,
                    { preserveScroll: true, onFinish: close }
                );
            },
        });

    return (
        <Segment>
            <SegmentHeader
                header={
                    <span className='inline-flex flex-wrap items-center gap-2'>
                        {entitlement.pso_category_label}
                        <Badge color={entitlement.category_color}>
                            {entitlement.category_label}
                        </Badge>
                    </span>
                }
            >
                {(canUpdate || canDelete) && (
                    <Menu>
                        <MenuButton aria-label='Acties'>
                            <EllipsisVerticalIcon className='size-5' />
                        </MenuButton>
                        <MenuItems anchor='bottom end'>
                            {canUpdate && (
                                <MenuItem
                                    icon={PencilIcon}
                                    onClick={() => onEdit(entitlement)}
                                >
                                    Wijzigen
                                </MenuItem>
                            )}
                            {canUpdate && canDelete && <MenuSeparator />}
                            {canDelete && (
                                <MenuItem
                                    className='text-red-600'
                                    icon={TrashIcon}
                                    onClick={handleDelete}
                                >
                                    Verwijderen
                                </MenuItem>
                            )}
                        </MenuItems>
                    </Menu>
                )}
            </SegmentHeader>
            <SegmentContent>
                <div className='flex flex-col gap-4'>
                    <Table striped>
                        <TableBody>
                            <TableRow>
                                <TableCell className='font-bold'>
                                    Startdatum
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {localDate(entitlement.start_date)}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className='font-bold'>
                                    Einddatum
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {entitlement.end_date
                                        ? localDate(entitlement.end_date)
                                        : 'Doorlopend'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    {employments.length === 0 ? (
                        <p className='text-sm text-gray-500'>
                            Er zijn geen dienstverbanden binnen de looptijd van
                            dit recht.
                        </p>
                    ) : (
                        <>
                            <Divider content='Dienstverbanden' />
                            <div className='flex flex-col gap-3'>
                                {employments.map(employment => (
                                    <EmploymentPeriods
                                        employment={employment}
                                        periods={employment.periods}
                                        hoursKey='contract_hours'
                                        hoursLabel='Contracturen'
                                        key={employment.id}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </SegmentContent>
        </Segment>
    );
};

export default SroiEntitlementCard;
